import { faUsers } from '@fortawesome/free-solid-svg-icons'
import { twMerge } from 'tailwind-merge'
import { useUser } from '~/features/auth/api/useUser'
import { useKillRaceInfo } from '../api/useKillRaceInfo'
import { useKillRaceLeaderboard } from '../api/useKillRaceLeaderboard'
import { TournamentEntry as Entry } from '../types'
import { formatUsd, rankTone, toNumber } from '../utils/killrace'
import { ACCENT, EmptyBlock, ErrorBlock, Panel, SectionHeading, Skeleton } from './KillRaceUI'

/**
 * Column widths live in one place so the header and every row stay in step.
 * `minmax(0,1fr)` on the player column is what lets a long username truncate
 * instead of pushing the numeric columns off the panel.
 */
const GRID = 'lg:grid lg:grid-cols-[3.5rem_minmax(0,1fr)_5rem_6rem_5rem_7rem_8rem] lg:items-center lg:gap-2'

export const KillRaceLeaderboard = ({ uuid }: { uuid: string }) => {
    const { data, isLoading, isError, refetch } = useKillRaceLeaderboard(uuid)
    const { data: info } = useKillRaceInfo(uuid)
    const { data: user } = useUser()

    const entries: Entry[] = Array.isArray(data) ? data : []

    // The standings stop at the last *paid* place, so a player who entered and
    // finished outside them is simply absent from `entries` and used to lose
    // their pinned row with it -- the one thing on the page that answers "where
    // did I come". The detail endpoint carries that row whether or not it made
    // the board, so fall back to it.
    const mine: Entry | undefined = entries.find((entry) => entry.user_id === user?.id) ?? info?.user_entry

    return (
        <section className='flex w-full flex-col gap-5'>
            <SectionHeading
                icon={faUsers}
                title='Standings'
                hint={entries.length > 0 ? `${entries.length} ranked` : undefined}
            />

            {/* Pinned copy of the viewer's own row. Padding mirrors the list
                panel below so the columns line up between the two panels. */}
            {mine && (
                <Panel border={ACCENT} size={14} className='p-3 md:p-4'>
                    <Row entry={mine} you highlight />
                    {mine.rank > entries.length && (
                        <p className='px-3 pb-1 text-2xs uppercase tracking-[0.15em] text-white/35'>
                            Your place, below the paid {entries.length}
                        </p>
                    )}
                </Panel>
            )}

            {isLoading && (
                <Panel className='flex flex-col gap-2 p-3 md:p-4'>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className='h-11 w-full' />
                    ))}
                </Panel>
            )}

            {isError && (
                <ErrorBlock title="Couldn't load the standings" onRetry={refetch}>
                    Kills are still being counted server-side. Only this table failed to load.
                </ErrorBlock>
            )}

            {!isLoading && !isError && entries.length === 0 && (
                <EmptyBlock title='No entries yet'>
                    Nobody has scored in this race so far. The first kill puts a name on this board.
                </EmptyBlock>
            )}

            {!isLoading && !isError && entries.length > 0 && (
                <Panel className='flex flex-col gap-2 p-3 md:p-4'>
                    <div className={twMerge('hidden px-3 pb-1', GRID, 'lg:grid')}>
                        {['Rank', 'Player', 'Kills', 'Sessions', 'K/D', 'Earned', 'Reward'].map((column, i) => (
                            <span
                                key={column}
                                className={twMerge(
                                    'text-2xs font-bold uppercase tracking-[0.2em] text-white/35',
                                    i > 1 && 'text-right'
                                )}
                            >
                                {column}
                            </span>
                        ))}
                    </div>
                    {entries.map((entry) => (
                        <Row key={entry.user_id} entry={entry} you={entry.user_id === user?.id} />
                    ))}
                </Panel>
            )}
        </section>
    )
}

const Cell = ({ label, children, className = undefined }) => (
    <div className={twMerge('flex items-center justify-between gap-2 lg:justify-end', className)}>
        <span className='text-2xs uppercase tracking-[0.2em] text-white/35 lg:hidden'>{label}</span>
        <span className='font-bold tabular-nums'>{children}</span>
    </div>
)

const Row = ({ entry, you = false, highlight = false }: { entry: Entry; you?: boolean; highlight?: boolean }) => {
    const tone = rankTone(entry.rank)
    const reward = toNumber(entry.potential_reward)

    return (
        <div
            className={twMerge(
                'flex flex-col gap-2 border border-white/10 px-3 py-3 text-sm transition-colors',
                'lg:gap-0 lg:border-0 lg:border-l-2 lg:py-2.5',
                GRID,
                highlight ? 'border-0 bg-transparent' : 'bg-white/[0.03] hover:bg-white/[0.07]',
                !highlight && you && 'bg-white/[0.09]'
            )}
            style={!highlight && entry.rank <= 3 ? { borderLeftColor: tone.hex } : undefined}
        >
            <div className='flex items-center gap-3 lg:gap-0'>
                <span className={twMerge('text-lg font-black tabular-nums lg:text-base', tone.text)}>{entry.rank}</span>
                <span className='truncate font-bold lg:hidden'>{entry.username}</span>
                {you && (
                    <span className='ml-auto border border-white/25 px-2 py-0.5 text-2xs font-black uppercase tracking-[0.2em] lg:hidden'>
                        You
                    </span>
                )}
            </div>

            <div className='hidden min-w-0 items-center gap-2 lg:flex'>
                <span className='truncate font-bold'>{entry.username}</span>
                {you && (
                    <span className='shrink-0 border border-white/25 px-1.5 py-0.5 text-2xs font-black uppercase tracking-[0.2em]'>
                        You
                    </span>
                )}
            </div>

            <div className='grid grid-cols-2 gap-x-4 gap-y-1 border-t border-white/10 pt-2 lg:contents lg:border-0 lg:pt-0'>
                <Cell label='Kills'>{entry.kills}</Cell>
                <Cell label='Sessions' className='text-white/55'>
                    {entry.sessions}
                </Cell>
                <Cell label='K/D' className='text-white/55'>
                    {entry.kill_death_ratio}
                </Cell>
                <Cell label='Earned' className='text-white/55'>
                    {formatUsd(entry.earnings)}
                </Cell>
                <Cell label='Reward' className={reward > 0 ? 'text-[#00FF90]' : 'text-white/25'}>
                    {formatUsd(reward)}
                </Cell>
            </div>
        </div>
    )
}
